class FBProductMain extends HTMLElement {
  connectedCallback() {
    if (this.dataset.initialized === 'true') return;
    this.dataset.initialized = 'true';

    this.variants = this.parseVariants();
    this.mediaItems = Array.from(this.querySelectorAll('[data-media-item]'));
    this.dots = Array.from(this.querySelectorAll('[data-gallery-dot]'));
    this.currentMediaIndex = Math.max(0, this.mediaItems.findIndex((item) => item.classList.contains('is-active')));
    this.optionButtons = Array.from(this.querySelectorAll('[data-option-index][data-option-value]'));
    this.variantInput = this.querySelector('[data-variant-id-input]');
    this.addButton = this.querySelector('[data-add-button]');
    this.addButtonText = this.querySelector('[data-add-button-text]');
    this.price = this.querySelector('[data-product-price]');
    this.comparePrice = this.querySelector('[data-product-compare-price]');
    this.inventory = this.querySelector('[data-inventory-state]');
    this.inventoryText = this.querySelector('[data-inventory-text]');
    this.quantityInput = this.querySelector('[data-quantity-input]');
    this.lightbox = this.querySelector('[data-lightbox]');
    this.lightboxImage = this.querySelector('[data-lightbox-image]');
    this.lightboxIndex = this.querySelector('[data-lightbox-index]');
    this.lightboxStage = this.querySelector('[data-lightbox-stage]');

    this.bindGallery();
    this.bindOptions();
    this.bindQuantity();
    this.bindLightbox();
    this.updateOptionAvailability();
  }

  parseVariants() {
    const source = this.querySelector('[data-fb-variants]');
    if (!source) return [];

    try {
      return JSON.parse(source.textContent || '[]');
    } catch (error) {
      console.error('[FB Product Main] No se pudieron leer las variantes.', error);
      return [];
    }
  }

  bindGallery() {
    this.querySelector('[data-gallery-prev]')?.addEventListener('click', () => this.showMedia(this.currentMediaIndex - 1));
    this.querySelector('[data-gallery-next]')?.addEventListener('click', () => this.showMedia(this.currentMediaIndex + 1));
    this.dots.forEach((dot) => dot.addEventListener('click', () => this.showMedia(Number(dot.dataset.galleryDot || 0))));

    const stage = this.querySelector('[data-gallery-stage]');
    this.bindSwipe(stage, () => this.showMedia(this.currentMediaIndex - 1), () => this.showMedia(this.currentMediaIndex + 1));

    this.addEventListener('keydown', (event) => {
      if (this.lightbox?.open) return;
      if (event.key === 'ArrowLeft') this.showMedia(this.currentMediaIndex - 1);
      if (event.key === 'ArrowRight') this.showMedia(this.currentMediaIndex + 1);
    });
  }

  bindSwipe(surface, previous, next) {
    if (!surface) return;

    let startX = 0;
    let startY = 0;

    surface.addEventListener('touchstart', (event) => {
      const touch = event.changedTouches[0];
      startX = touch.clientX;
      startY = touch.clientY;
    }, { passive: true });

    surface.addEventListener('touchend', (event) => {
      const touch = event.changedTouches[0];
      const deltaX = touch.clientX - startX;
      const deltaY = touch.clientY - startY;

      if (Math.abs(deltaX) < 45 || Math.abs(deltaX) <= Math.abs(deltaY)) return;
      if (deltaX > 0) previous();
      else next();
    }, { passive: true });
  }

  normalizeMediaIndex(indexOrId) {
    if (!this.mediaItems.length) return 0;

    let nextIndex;
    if (typeof indexOrId === 'string') {
      const mediaIndex = this.mediaItems.findIndex((item) => item.dataset.mediaId === indexOrId);
      nextIndex = mediaIndex >= 0 ? mediaIndex : Number(indexOrId);
    } else {
      nextIndex = Number(indexOrId);
    }

    if (Number.isNaN(nextIndex)) return this.currentMediaIndex;
    if (nextIndex < 0) return this.mediaItems.length - 1;
    if (nextIndex >= this.mediaItems.length) return 0;
    return nextIndex;
  }

  showMedia(indexOrId) {
    if (!this.mediaItems.length) return;
    const nextIndex = this.normalizeMediaIndex(indexOrId);

    this.mediaItems.forEach((item, index) => {
      const active = index === nextIndex;
      item.hidden = !active;
      item.classList.toggle('is-active', active);

      if (!active) {
        item.querySelectorAll('video').forEach((video) => video.pause());
      }
    });

    this.dots.forEach((dot, index) => {
      const active = index === nextIndex;
      dot.classList.toggle('is-active', active);
      dot.setAttribute('aria-selected', active ? 'true' : 'false');
    });

    this.currentMediaIndex = nextIndex;

    const zoomButton = this.querySelector('[data-gallery-zoom]');
    if (zoomButton) zoomButton.hidden = !this.mediaItems[nextIndex]?.dataset.zoomSrc;
  }

  bindOptions() {
    this.optionButtons.forEach((button) => {
      button.addEventListener('click', () => {
        if (button.disabled) return;

        const group = button.closest('[data-option-group]');
        group?.querySelectorAll('[data-option-value]').forEach((choice) => {
          const selected = choice === button;
          choice.classList.toggle('is-selected', selected);
          choice.setAttribute('aria-pressed', selected ? 'true' : 'false');
        });

        const selectedLabel = group?.querySelector('[data-option-selected]');
        if (selectedLabel) selectedLabel.textContent = button.dataset.optionValue || '';

        this.resolveVariant();
      });
    });
  }

  selectedOptions() {
    const groups = Array.from(this.querySelectorAll('[data-option-group]'));
    return groups.map((group) => group.querySelector('.is-selected[data-option-value]')?.dataset.optionValue || '');
  }

  resolveVariant() {
    const selected = this.selectedOptions();
    const variant = this.variants.find((item) => item.options.every((option, index) => option === selected[index]));
    this.updateVariant(variant || null);
    this.updateOptionAvailability();
  }

  updateVariant(variant) {
    if (!variant) {
      if (this.variantInput) this.variantInput.value = '';
      if (this.addButton) this.addButton.disabled = true;
      if (this.addButtonText) this.addButtonText.textContent = this.dataset.unavailableLabel || 'No disponible';
      this.setInventory('sold-out', this.dataset.unavailableLabel || 'No disponible');
      return;
    }

    if (this.variantInput) {
      this.variantInput.value = String(variant.id);
      this.variantInput.dispatchEvent(new Event('change', { bubbles: true }));
    }

    if (this.price) this.price.textContent = variant.price || '';

    if (this.comparePrice) {
      const showCompare = Number(variant.compareAtPriceCents || 0) > Number(variant.priceCents || 0);
      this.comparePrice.hidden = !showCompare;
      this.comparePrice.textContent = showCompare ? (variant.compareAtPrice || '') : '';
    }

    if (this.addButton) this.addButton.disabled = !variant.available;

    if (this.addButtonText) {
      this.addButtonText.textContent = variant.available
        ? (this.dataset.addLabel || 'Agregar al carrito')
        : (this.dataset.soldOutLabel || 'Agotado');
    }

    const addComponent = this.querySelector('add-to-cart-component');
    if (addComponent && variant.featuredMediaPreview) {
      addComponent.dataset.productVariantMedia = variant.featuredMediaPreview;
    }

    if (!variant.available) {
      this.setInventory('sold-out', this.dataset.soldOutLabel || 'Agotado');
    } else if (variant.inventoryManagement === 'shopify' && Number(variant.inventoryQuantity) > 0) {
      const template = this.dataset.inventoryTemplate || '[cantidad] disponibles';
      this.setInventory('in-stock', template.replace('[cantidad]', String(variant.inventoryQuantity)));
    } else {
      this.setInventory('available', this.dataset.availableLabel || 'Disponible');
    }

    if (this.quantityInput) {
      this.quantityInput.min = String(variant.quantityMin || 1);
      this.quantityInput.step = String(variant.quantityStep || 1);

      if (variant.quantityMax) this.quantityInput.max = String(variant.quantityMax);
      else this.quantityInput.removeAttribute('max');

      if (Number(this.quantityInput.value) < Number(this.quantityInput.min)) {
        this.quantityInput.value = this.quantityInput.min;
      }

      if (this.quantityInput.max && Number(this.quantityInput.value) > Number(this.quantityInput.max)) {
        this.quantityInput.value = this.quantityInput.max;
      }
    }

    if (variant.featuredMediaId) this.showMedia(String(variant.featuredMediaId));

    if (this.dataset.updateUrl === 'true') {
      const url = new URL(window.location.href);
      url.searchParams.set('variant', variant.id);
      window.history.replaceState({}, '', url.toString());
    }

    this.dispatchEvent(new CustomEvent('fb:variant-change', { bubbles: true, detail: { variant } }));
  }

  setInventory(state, text) {
    if (!this.inventory || !this.inventoryText) return;

    this.inventory.dataset.inventoryState = state;
    this.inventory.classList.remove(
      'fb-product-main__inventory--in-stock',
      'fb-product-main__inventory--available',
      'fb-product-main__inventory--sold-out'
    );
    this.inventory.classList.add(`fb-product-main__inventory--${state}`);
    this.inventoryText.textContent = text;
  }

  updateOptionAvailability() {
    const selected = this.selectedOptions();

    this.optionButtons.forEach((button) => {
      const index = Number(button.dataset.optionIndex);
      const candidate = button.dataset.optionValue || '';
      const possible = this.variants.some((variant) => {
        if (!variant.available || variant.options[index] !== candidate) return false;
        return variant.options.every((value, optionIndex) => (
          optionIndex === index || !selected[optionIndex] || value === selected[optionIndex]
        ));
      });

      button.disabled = !possible;
      button.setAttribute('aria-disabled', possible ? 'false' : 'true');
    });
  }

  bindQuantity() {
    const minus = this.querySelector('[data-quantity-minus]');
    const plus = this.querySelector('[data-quantity-plus]');

    minus?.addEventListener('click', () => this.changeQuantity(-1));
    plus?.addEventListener('click', () => this.changeQuantity(1));
    this.quantityInput?.addEventListener('change', () => this.clampQuantity());
  }

  changeQuantity(direction) {
    if (!this.quantityInput) return;

    const step = Number(this.quantityInput.step || 1);
    this.quantityInput.value = String(Number(this.quantityInput.value || 0) + direction * step);
    this.clampQuantity();
    this.quantityInput.dispatchEvent(new Event('change', { bubbles: true }));
  }

  clampQuantity() {
    if (!this.quantityInput) return;

    const min = Number(this.quantityInput.min || 1);
    const max = this.quantityInput.max ? Number(this.quantityInput.max) : Infinity;
    let value = Number(this.quantityInput.value || min);

    if (!Number.isFinite(value)) value = min;
    this.quantityInput.value = String(Math.min(max, Math.max(min, value)));
  }

  bindLightbox() {
    this.querySelector('[data-gallery-zoom]')?.addEventListener('click', () => this.openLightbox());
    this.querySelector('[data-lightbox-prev]')?.addEventListener('click', () => this.changeLightboxMedia(-1));
    this.querySelector('[data-lightbox-next]')?.addEventListener('click', () => this.changeLightboxMedia(1));
    this.querySelector('[data-lightbox-close]')?.addEventListener('click', () => this.closeLightbox());

    this.bindSwipe(
      this.lightboxStage,
      () => this.changeLightboxMedia(-1),
      () => this.changeLightboxMedia(1)
    );

    this.lightbox?.addEventListener('click', (event) => {
      if (event.target === this.lightbox) this.closeLightbox();
    });

    this.lightbox?.addEventListener('cancel', () => {
      document.documentElement.classList.remove('fb-lightbox-open');
    });

    document.addEventListener('keydown', (event) => {
      if (!this.lightbox?.open) return;
      if (event.key === 'ArrowLeft') this.changeLightboxMedia(-1);
      if (event.key === 'ArrowRight') this.changeLightboxMedia(1);
    });
  }

  openLightbox() {
    if (!this.lightbox || !this.lightboxImage) return;

    const active = this.mediaItems[this.currentMediaIndex];
    if (!active?.dataset.zoomSrc) return;

    this.setLightboxMedia(this.currentMediaIndex);
    document.documentElement.classList.add('fb-lightbox-open');

    if (typeof this.lightbox.showModal === 'function') this.lightbox.showModal();
    else this.lightbox.setAttribute('open', '');

    this.querySelector('[data-lightbox-close]')?.focus({ preventScroll: true });
  }

  changeLightboxMedia(direction) {
    if (!this.mediaItems.length) return;

    let nextIndex = this.currentMediaIndex;
    let attempts = 0;

    do {
      nextIndex += direction;
      if (nextIndex < 0) nextIndex = this.mediaItems.length - 1;
      if (nextIndex >= this.mediaItems.length) nextIndex = 0;
      attempts += 1;
    } while (!this.mediaItems[nextIndex]?.dataset.zoomSrc && attempts <= this.mediaItems.length);

    if (attempts > this.mediaItems.length) return;
    this.showMedia(nextIndex);
    this.setLightboxMedia(nextIndex);
  }

  setLightboxMedia(index) {
    const item = this.mediaItems[index];
    if (!item || !this.lightboxImage || !item.dataset.zoomSrc) return;

    this.lightboxImage.src = item.dataset.zoomSrc;
    this.lightboxImage.alt = item.dataset.zoomAlt || '';

    if (item.dataset.zoomWidth) this.lightboxImage.width = Number(item.dataset.zoomWidth);
    if (item.dataset.zoomHeight) this.lightboxImage.height = Number(item.dataset.zoomHeight);
    if (this.lightboxIndex) this.lightboxIndex.textContent = String(index + 1);
  }

  closeLightbox() {
    if (!this.lightbox) return;

    document.documentElement.classList.remove('fb-lightbox-open');

    if (typeof this.lightbox.close === 'function') this.lightbox.close();
    else this.lightbox.removeAttribute('open');
  }
}

if (!customElements.get('fb-product-main')) {
  customElements.define('fb-product-main', FBProductMain);
}
